import { SafePromise } from '@benzinga/safe-await';
import { Subscribable, Subscription } from '@benzinga/subscribable';
import { Session, AuthenticationManager } from '@benzinga/session';

import {
  NotificationHistoryParams,
  NotificationResponse,
  Notification,
  ReadStatus,
  NotificationMarkReadUnreadParams,
  NotificationReadUpdateParams,
  NotificationConfigParams,
  MarketNotificationFrequency,
  MarketNotificationFrequencyRes,
  GetMarketNotificationFrequencyResponse,
  UpdateGlobalNotificationSettings,
  FetchGlobalNotificationSettings,
  UpdateGlobalNotificationSettingsParams,
  EditorialMarketAlertsNotificationParams,
  SendGroupDataResponse,
  EditorialMarketAlertsNotificationResponse,
  EditorialMarketAlertsAnalyticsResponse,
  SendGroupData,
  NotificationConfigGetResponse,
  NotificationRegisterResponse,
  RegisterTokenParams,
  NotificationHistoryResponse,
} from './entities';
import {
  NotificationRequest,
  NotificationRestfulEvent,
  NotificationsHistoryEvent,
  NotificationsMarkReadUnreadEvent,
} from './request';
import { NotificationSocket, NotificationSocketEvent, NotificationReceivedEvent } from './socket';
import { NotificationsStore } from './store';
import { MARKET_ALERTS_LIST } from './marketAlertsList';
import { NotificationEnvironment } from './environment';

interface NotificationsUpdateEvent {
  type: 'notifications:update';
}

export type NotificationsManagerEvent = NotificationRestfulEvent | NotificationSocketEvent | NotificationsUpdateEvent;

export class NotificationManager extends Subscribable<NotificationsManagerEvent> {
  private authenticationManager: AuthenticationManager;
  private request: NotificationRequest;
  private socket: NotificationSocket;
  private store: NotificationsStore;
  private socketSubscription?: Subscription<NotificationSocket>;
  private requestSubscription?: Subscription<NotificationRequest>;
  private session: Session;
  private authSubscription?: Subscription<AuthenticationManager>;
  private userId: number | undefined;
  private pushSubscription?: PushSubscription;
  private pushGlobalKey?: string;
  private pushDeviceRegistered: boolean;

  constructor(session: Session) {
    super();
    this.session = session;
    this.request = new NotificationRequest(session);
    this.socket = new NotificationSocket(session);
    this.store = new NotificationsStore();
    this.authenticationManager = this.session.getManager(AuthenticationManager);
    this.pushDeviceRegistered = false;
  }

  public static getName = () => 'benzinga-notification';

  public registerToken = async (params: RegisterTokenParams) => {
    return this.request.registerToken(params);
  };

  public getNotificationHistory = async (
    params: NotificationHistoryParams,
  ): SafePromise<NotificationHistoryResponse | undefined> => {
    return this.request.getNotificationHistory(params);
  };

  public queryNotificationHistory = (
    params: NotificationHistoryParams,
  ): SafePromise<NotificationHistoryResponse> => {
    return this.request.queryNotificationHistory(params);
  };

  public getConfigurations = async (): SafePromise<NotificationConfigGetResponse> => {
    return this.request.getConfigurations();
  };

  public setConfigurations = async (
    params: NotificationConfigParams,
  ): SafePromise<NotificationResponse | undefined> => {
    return this.request.setConfigurations(params);
  };

  public getMarketNotificationFrequency = async (): SafePromise<GetMarketNotificationFrequencyResponse> => {
    return this.request.getMarketNotificationFrequency();
  };

  public setMarketNotificationFrequency = async (
    params: MarketNotificationFrequency,
  ): SafePromise<MarketNotificationFrequencyRes | undefined> => {
    return this.request.setMarketNotificationFrequency(params);
  };

  public fetchGlobalNotificationSettings = async (): SafePromise<FetchGlobalNotificationSettings> => {
    return this.request.fetchGlobalNotificationSettings();
  };

  public updateGlobalNotificationSettings = async (
    params: UpdateGlobalNotificationSettingsParams,
  ): SafePromise<UpdateGlobalNotificationSettings> => {
    return this.request.updateGlobalNotificationSettings(params);
  };

  public sendEditorialMarketAlertsNotification = async (
    params: EditorialMarketAlertsNotificationParams,
  ): SafePromise<EditorialMarketAlertsNotificationResponse> => {
    return this.request.sendEditorialMarketAlertsNotification(params);
  };

  public getMarketAlertsAnalytics = async (): SafePromise<EditorialMarketAlertsAnalyticsResponse> => {
    return this.request.getMarketAlertsAnalytics();
  };

  public getStaticSendGroupData = (): SendGroupData[] => {
    return MARKET_ALERTS_LIST;
  };

  public getSendGroupData = async (force?: boolean): SafePromise<SendGroupDataResponse> => {
    if (force) {
      const response = await this.request.getSendGroupData();
      return response;
    } else {
      return {
        ok: {
          data: MARKET_ALERTS_LIST,
          ok: true,
        },
      };
    }
  };

  public markReadUnread = (read_ids: number[], unread_ids: number[] = [], read_all = false): void => {
    const bz_uid = this.authenticationManager.getAuthentication()?.user.benzingaUid;
    const params: NotificationMarkReadUnreadParams = {
      bz_uid,
      read_all,
      read_ids,
      unread_ids,
    };
    this.request.markReadUnread(params);
  };

  public updateNotificationRead = async (
    params: NotificationReadUpdateParams,
  ): SafePromise<NotificationResponse | undefined> => {
    return this.request.updateNotificationRead(params);
  };

  public getNotifications = (): Notification[] => this.store.getNotifications();

  public loadMore = (status: ReadStatus, limit = 50) => {
    if (this.store.hasMore(status)) {
      const params: NotificationHistoryParams = {
        limit,
        read_status: status,
      };
      const lastId = this.store.getLastId(status);
      if (lastId) {
        params.last_id = lastId;
      }
      this.request.getNotificationHistory(params);
    }
  };

  public getUnreadCount = (): number | undefined => this.store.getUnreadCount();
  public getTotalCount = (): number | undefined => this.store.getTotalCount();

  public hasMore = (status: ReadStatus) => this.store.hasMore(status);
  public isPushRegistered = (): boolean => this.pushDeviceRegistered;

  public registerPushDevice = async (anonymous = false): SafePromise<NotificationRegisterResponse> => {
    try {
      if (!this.pushSubscription) {
        return {
          ok: {
            success: false,
          },
        };
      }

      const token = this.authenticationManager.getAuthentication()?.user?.benzingaUid;
      const res = await this.request.registerPushDevice({
        benzinga_uid: anonymous ? undefined : token,
        global_site_key: this.pushGlobalKey,
        push: this.pushSubscription,
      });
      if (!res?.ok?.success) {
        navigator.serviceWorker.ready.then(reg => {
          reg.pushManager.getSubscription().then(sub => {
            sub?.unsubscribe();
          });
        });
      } else {
        this.pushDeviceRegistered = true;
      }

      return res;
    } catch (_err) {
      return {
        ok: {
          success: false,
        },
      };
    }
  };

  public unregisterPushDevice = async (): SafePromise<NotificationRegisterResponse> => {
    if (!this.pushSubscription) {
      return {
        ok: {
          success: false,
        },
      };
    }
    try {
      return this.request.unregisterPushDevice({ push: this.pushSubscription });
    } catch (_err) {
      return {
        ok: {
          success: false,
        },
      };
    }
  };

  /**
   * Hydrate push state from an existing browser subscription (e.g. after full page load).
   */
  public async syncPushSubscriptionFromBrowser(globalKey?: string): Promise<void> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    try {
      // Use getRegistration — `serviceWorker.ready` never resolves if no SW was ever registered
      // (e.g. user opens push modal and clicks "No Thanks" without subscribing).
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        return;
      }
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        this.pushSubscription = existing;
        if (globalKey) {
          this.pushGlobalKey = globalKey;
        } else if (!this.pushGlobalKey && typeof window !== 'undefined') {
          this.pushGlobalKey = window.location.host;
        }
        this.pushDeviceRegistered = true;
      }
    } catch {
      /* ignore */
    }
  }

  /**
   * Unregister with the backend (best effort), unsubscribe in the browser, and clear local state.
   */
  public unsubscribePushFully = async (): SafePromise<NotificationRegisterResponse> => {
    await this.syncPushSubscriptionFromBrowser();
    const sub = this.pushSubscription;
    if (!sub) {
      this.pushSubscription = undefined;
      this.pushDeviceRegistered = false;
      this.pushGlobalKey = undefined;
      return { ok: { success: true } };
    }
    await this.unregisterPushDevice();
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
    this.pushSubscription = undefined;
    this.pushDeviceRegistered = false;
    this.pushGlobalKey = undefined;
    return { ok: { success: true } };
  };

  /**
   * POST subscription JSON to a same-origin API (or proxy) that forwards to the push service.
   */
  public sendTestPushNotification = async (postUrl: string): SafePromise<NotificationRegisterResponse> => {
    await this.syncPushSubscriptionFromBrowser();
    const sub = this.pushSubscription;
    if (!sub) {
      return { ok: { success: false } };
    }
    const pushPayload = sub.toJSON();
    try {
      const res = await fetch(postUrl, {
        body: JSON.stringify({ push: pushPayload }),
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      const data = (await res.json().catch(() => ({}))) as { success?: boolean };
      return { ok: { success: Boolean(res.ok && data?.success) } };
    } catch {
      return { ok: { success: false } };
    }
  };

  public setupServiceWorker = async ({ global_key }) => {
    if ('serviceWorker' in navigator) {
      await navigator.serviceWorker.register(`${window.location.origin}/sw.js`);
      const sw = await navigator.serviceWorker.ready;
      this.pushGlobalKey = global_key;

      const oldPush = await sw.pushManager.getSubscription();
      if (!oldPush) {
        const newPush = await sw.pushManager.subscribe({
          applicationServerKey: this.session.getEnvironment(NotificationEnvironment).vapidPublicKey,
          userVisibleOnly: true,
        });
        this.pushSubscription = newPush;
        return this.registerPushDevice();
      } else {
        this.pushSubscription = oldPush;
        this.pushDeviceRegistered = true;
        return {
          ok: {
            success: true,
          },
        };
      }
    } else {
      console.log('service worker not supported');
      return {
        ok: {
          success: false,
        },
      };
    }
  };

  protected onFirstSubscription(): void {
    this.socketSubscription = this.socket.listen(event => this.handleEvent(event));
    this.requestSubscription = this.request.listen(event => this.handleEvent(event));
    this.authSubscription = this.authenticationManager.subscribe(event => {
      switch (event.type) {
        case 'authentication:logged_in': {
          const benzingaUid = this.authenticationManager.getAuthentication()?.user.benzingaUid;
          const token = this.authenticationManager.getBenzingaToken();
          if (benzingaUid && token) {
            this.loadMore('unread');
            this.registerPushDevice();
            if (this.userId === benzingaUid) {
              this.socket.reconnect();
            } else {
              this.socket.createSocket(token);
            }
          }
          this.userId = benzingaUid;
          break;
        }
        case 'authentication:logged_out': {
          this.socket.close();
          this.clearNotificationsData();
          this.registerPushDevice();
        }
      }
    });
    this.start();
  }

  protected onZeroSubscriptions(): void {
    this.socketSubscription?.unsubscribe();
    this.requestSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.stop();
  }

  private start = (): void => {
    const token = this.authenticationManager.getBenzingaToken();
    if (token) {
      this.loadMore('unread');
      this.socket.createSocket(token);
    }
    // If no token, wait for login - the authentication subscription will handle starting the socket
  };

  private stop = (): void => {
    this.socket.clear();
    this.clearNotificationsData();
  };

  private clearNotificationsData = () => {
    this.store.clear();
    this.dispatch({ type: 'notifications:update' });
  };

  private handleEvent = (event: NotificationsManagerEvent) => {
    switch (event.type) {
      case 'notifications:get_history': {
        const {
          data: { notifications, total, unread_count },
          limit,
          status,
        } = event as NotificationsHistoryEvent;
        const lastId = notifications[notifications.length - 1]?.id;
        const previousLastId = this.store.getLastId(status);
        if (lastId < (previousLastId ?? Infinity)) {
          this.store.addNotifications(notifications);
          this.store.setTotalCount(total);
          if (previousLastId === undefined) {
            this.store.setUnreadCount(unread_count);
          }
          this.store.setLastId(notifications[notifications.length - 1]?.id, status);
          if (limit > notifications.length) {
            this.store.setFullyLoaded(status);
          }
          this.dispatch({ type: 'notifications:update' });
        }
        break;
      }
      case 'notifications:notification_received': {
        const { notification } = event as NotificationReceivedEvent;
        this.store.addNewNotification(notification);
        this.dispatch({ type: 'notifications:update' });
        break;
      }
      case 'notifications:mark_read_unread': {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { type, ...params } = event as NotificationsMarkReadUnreadEvent;
        this.store.markReadUnread(params);
        this.dispatch({ type: 'notifications:update' });
        break;
      }
    }
    this.dispatch(event);
  };
}
